// Account (simple mode). S1 ships the Developer mode switch (D29/D30); S3 adds name,
// password, sign out everywhere and theme over the existing /me/* routes
// (gateway/api/profile.py):
//
//   PATCH /me/profile        {first_name?, last_name?}
//   POST  /me/password       {current_password, new_password}  401 wrong current; 422 invalid new
//   POST  /me/sign-out-all   revokes every OTHER session; this one stays signed in
//   PATCH /me/preferences    {theme}  → <html data-theme> (ModeContext applies it)
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { PasswordChangeResult, Theme, User, UserPreferences } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useMode } from '../context/ModeContext'
import { ConfirmAction } from '../components/ConfirmAction'
import { ErrorPanel, Loading, PageHeader } from '../components/ui'

function NameSection() {
  const { session, patchUser } = useSession()
  const user = session?.user
  const [first, setFirst] = useState(user?.first_name ?? '')
  const [last, setLast] = useState(user?.last_name ?? '')
  const save = useMutation({
    mutationFn: (body: { first_name: string; last_name: string }) =>
      apiFetch<Pick<User, 'first_name' | 'last_name'>>('/me/profile', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (data, body) => patchUser({ first_name: data?.first_name ?? body.first_name, last_name: data?.last_name ?? body.last_name }),
  })
  const unchanged = first.trim() === (user?.first_name ?? '') && last.trim() === (user?.last_name ?? '')
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (unchanged || save.isPending) return
    save.mutate({ first_name: first.trim(), last_name: last.trim() })
  }
  return (
    <section className="card-section" aria-labelledby="acct-name-title">
      <h2 id="acct-name-title" className="section-title">Name</h2>
      <form aria-label="Your name" onSubmit={onSubmit}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="acct-first">First name</label>
            <input id="acct-first" autoComplete="given-name" value={first} onChange={(e) => { save.reset(); setFirst(e.target.value) }} />
          </div>
          <div className="field">
            <label htmlFor="acct-last">Last name</label>
            <input id="acct-last" autoComplete="family-name" value={last} onChange={(e) => { save.reset(); setLast(e.target.value) }} />
          </div>
        </div>
        <button type="submit" className="btn btn--primary" disabled={unchanged || save.isPending} aria-busy={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save name'}
        </button>
      </form>
      {save.isSuccess && <p className="muted" role="status">Name saved.</p>}
      {save.error ? <p className="form-error" role="alert">{errorMessage(save.error)}</p> : null}
    </section>
  )
}

function passwordError(err: unknown): string {
  if (isApiError(err) && err.status === 401) return 'Your current password is wrong.'
  return errorMessage(err)
}

function PasswordSection() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [hint, setHint] = useState<string | null>(null)
  const change = useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      apiFetch<PasswordChangeResult>('/me/password', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
    },
  })
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (change.isPending) return
    change.reset()
    // The gateway re-checks both rules (422); these only save a round trip.
    if (next.length < 8) return setHint('The new password must be at least 8 characters.')
    if (next === current) return setHint('The new password must differ from the current one.')
    setHint(null)
    change.mutate({ current_password: current, new_password: next })
  }
  const result = change.data
  return (
    <section className="card-section" aria-labelledby="acct-pw-title">
      <h2 id="acct-pw-title" className="section-title">Password</h2>
      <form aria-label="Change password" onSubmit={onSubmit} noValidate>
        <div className="field">
          <label htmlFor="acct-pw-current">Current password</label>
          <input id="acct-pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="acct-pw-new">New password</label>
          <input id="acct-pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
          <span className="field-hint">At least 8 characters. Your other sessions are signed out.</span>
        </div>
        <button type="submit" className="btn btn--primary" disabled={!current || !next || change.isPending} aria-busy={change.isPending}>
          {change.isPending ? 'Changing…' : 'Change password'}
        </button>
      </form>
      {hint && <p className="form-error" role="alert">{hint}</p>}
      {change.error ? <p className="form-error" role="alert">{passwordError(change.error)}</p> : null}
      {result && (
        <p className={result.other_sessions_revoked ? 'muted' : 'warn-note'} role="status">
          {result.other_sessions_revoked
            ? 'Password changed. Your other sessions were signed out.'
            : 'Password changed, but other sessions could not be signed out. Use “Sign out everywhere else” below.'}
        </p>
      )}
    </section>
  )
}

function SignOutEverywhereSection() {
  const [confirming, setConfirming] = useState(false)
  const signOut = useMutation({
    mutationFn: () => apiFetch<{ revoked: boolean }>('/me/sign-out-all', { method: 'POST' }),
    onSuccess: () => setConfirming(false),
  })
  return (
    <section className="card-section" aria-labelledby="acct-signout-title">
      <h2 id="acct-signout-title" className="section-title">Sessions</h2>
      <p className="muted">Sign out of Symone on every other browser and device. You stay signed in here.</p>
      <div>
        <button type="button" className="btn" onClick={() => { signOut.reset(); setConfirming(true) }}>
          Sign out everywhere else
        </button>
      </div>
      {signOut.isSuccess && <p className="muted" role="status">Signed out of every other session.</p>}
      {confirming && (
        <ConfirmAction
          title="Sign out everywhere else"
          confirmLabel="Sign out other sessions"
          pendingLabel="Signing out…"
          pending={signOut.isPending}
          error={signOut.error ? errorMessage(signOut.error) : undefined}
          onConfirm={() => signOut.mutate()}
          onClose={() => setConfirming(false)}
        >
          <p>Every other browser and device signed in to your account is signed out now.</p>
        </ConfirmAction>
      )}
    </section>
  )
}

const THEMES: [Theme, string][] = [
  ['system', 'Match my system'],
  ['light', 'Light'],
  ['dark', 'Dark'],
]

function ThemeSection() {
  const qc = useQueryClient()
  // The same cache entry ModeContext reads; it applies data-theme when this changes.
  const prefs = useQuery({
    queryKey: qk.myPreferences,
    queryFn: () => apiFetch<UserPreferences>('/me/preferences'),
  })
  const save = useMutation({
    mutationFn: (theme: Theme) =>
      apiFetch<UserPreferences>('/me/preferences', { method: 'PATCH', body: JSON.stringify({ theme }) }),
    onSuccess: (data, theme) =>
      qc.setQueryData<UserPreferences>(qk.myPreferences, (old) => data ?? (old ? { ...old, theme } : old)),
  })
  if (prefs.isLoading) return <Loading label="Loading your preferences" />
  if (prefs.error) return <ErrorPanel error={prefs.error} onRetry={() => prefs.refetch()} />
  const current = prefs.data?.theme ?? 'system'
  return (
    <>
      <fieldset className="theme-options" disabled={save.isPending} aria-busy={save.isPending}>
        <legend className="sr-only">Theme</legend>
        {THEMES.map(([value, label]) => (
          <label key={value} className="switch-row">
            <input
              type="radio"
              name="theme"
              value={value}
              checked={current === value}
              onChange={() => {
                save.reset()
                save.mutate(value)
              }}
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      {save.error ? <p className="form-error" role="alert">Couldn’t save the theme. {errorMessage(save.error)}</p> : null}
    </>
  )
}

function DeveloperModeSection() {
  const mode = useMode()
  const [error, setError] = useState<string | null>(null)

  if (mode.loading) return <Loading label="Checking your plan" />
  if (mode.error) return <ErrorPanel error={mode.error} onRetry={mode.retry} />
  if (!mode.entitled) {
    return <p className="muted" role="note">Developer mode is not included in this plan.</p>
  }
  return (
    <>
      <label className="switch-row">
        <input
          type="checkbox"
          role="switch"
          aria-describedby="dev-mode-desc"
          checked={mode.preference}
          disabled={mode.saving}
          onChange={(e) => {
            setError(null)
            mode.setDeveloperMode(e.target.checked).catch(() => setError('Couldn’t save. Try again.'))
          }}
        />
        <span className="cell-strong">Developer mode</span>
      </label>
      <p id="dev-mode-desc" className="muted">
        Adds the developer console to the sidebar: Playground, API keys, sessions, triggers, logs
        and more. Remembered on every device you sign in from.
      </p>
      {error && <p className="form-error" role="alert">{error}</p>}
    </>
  )
}

export function AccountView() {
  const { session } = useSession()
  const user = session?.user
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
  return (
    <div className="page page--narrow">
      <PageHeader title="Account" description={name ? `${name} · ${user?.email ?? ''}` : user?.email} />
      <NameSection />
      <PasswordSection />
      <SignOutEverywhereSection />
      <section className="card-section" aria-labelledby="theme-title">
        <h2 id="theme-title" className="section-title">Theme</h2>
        <ThemeSection />
      </section>
      <section className="card-section" aria-labelledby="dev-mode-title">
        <h2 id="dev-mode-title" className="section-title">Developer mode</h2>
        <DeveloperModeSection />
      </section>
    </div>
  )
}
